import { useNavigate } from "react-router-dom";
import { Plus, Users, UserPlus, UsersRound, CheckSquare, Upload, Receipt, Lightbulb } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { PRODUCT_CREATE_KEYS } from "../lib/product";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";
import { MobileSheet, MobileSheetBody, MobileSheetClose, MobileSheetCloseButton, MobileSheetContent, MobileSheetHeader, MobileSheetTitle, MobileSheetTrigger } from "./ui/mobile-sheet";
import useIsMobile from "../hooks/useIsMobile";

const ACTIONS = [
  { to: "/prospects/nouveau", label: "Nouveau prospect", icon: Users, permission: "prospects:create" },
  { to: "/tasks", label: "Nouvelle tâche", icon: CheckSquare, permission: "tasks:create" },
  { to: "/import", label: "Importer des prospects", icon: Upload, permission: "csv:import" },
  { to: "/users/nouveau", label: "Nouvel utilisateur", icon: UserPlus, permission: "users:create" },
  { to: "/collaborateurs/nouveau", label: "Nouveau collaborateur", icon: UsersRound, permission: "collaborators:create", feature: "collaborators" },
  { to: "/finance/depenses/nouvelle", label: "Nouvelle dépense", icon: Receipt, permission: "finance:create_expense", feature: "finance" },
  { to: "/produit/nouveau", label: "Nouveau retour produit", icon: Lightbulb, permission: PRODUCT_CREATE_KEYS, feature: "product", testid: "product-new" },
];

export default function QuickActionsMenu() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const items = ACTIONS.filter((a) =>
    (!a.adminOnly || user?.role === "admin") &&
    (!a.permission || hasPermission(user, a.permission)) &&
    (!a.feature || user?.features?.[a.feature])
  );

  // Nothing to offer this user: no dead button sitting in the header.
  if (items.length === 0) return null;

  const trigger = (
    <button type="button" data-testid="btn-quick-actions" aria-label="Actions rapides"
      className="h-10 w-10 flex items-center justify-center rounded-lg bg-primary/10 text-jamora hover:bg-primary/15 transition">
      <Plus size={20} />
    </button>
  );

  if (isMobile) return (
    <MobileSheet>
      <MobileSheetTrigger asChild>{trigger}</MobileSheetTrigger>
      <MobileSheetContent aria-describedby={undefined}>
        <MobileSheetHeader>
          <MobileSheetTitle>Actions rapides</MobileSheetTitle>
          <MobileSheetCloseButton />
        </MobileSheetHeader>
        <MobileSheetBody>
          <div className="space-y-1">
            {items.map((a) => <MobileSheetClose asChild key={a.to}>
              <button type="button" data-testid={`quick-action-${a.testid || a.permission || "users:create"}`}
                onClick={() => navigate(a.to, a.openCreate || a.to === "/tasks" ? { state: { openCreate: true } } : undefined)}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm text-slate-700 hover:bg-slate-100">
                <a.icon size={18} className="text-slate-500" /> {a.label}
              </button>
            </MobileSheetClose>)}
          </div>
        </MobileSheetBody>
      </MobileSheetContent>
    </MobileSheet>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {items.map((a) => (
          <DropdownMenuItem
            key={a.to} data-testid={`quick-action-${a.testid || a.permission || "users:create"}`}
            onClick={() => navigate(a.to, a.openCreate || a.to === "/tasks" ? { state: { openCreate: true } } : undefined)}
          >
            <a.icon size={16} className="text-slate-400" />
            {a.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
